import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { useMutation } from '@tanstack/react-query';
import { JOURNEE_PAR_DEFAUT, OU } from '@nextilt/commun';
import { api } from '../../partage/api/client';
import type { DossierDe, Ou, PossibleDuJour } from '../../partage/api/types';
import { useEchap } from '../../partage/useEchap';
import {
  A_AJOUTER,
  A_LA_SUITE,
  AJOUTE_CE_QUE_TU_VEUX,
  ajouterALaJournee,
  APRES_MINUIT,
  C_EST_MA_JOURNEE,
  CE_QUI_FLOTTE,
  changerLHeureDe,
  CHERCHER_UNE_TACHE,
  choisiesEtPossibles,
  cocherDansLaJournee,
  DEFAIRE_CETTE_JOURNEE,
  DEFAIRE_NOTE,
  desHeure,
  echeanceDe,
  FAITE,
  FINIRAIT_APRES_MINUIT,
  FIXEE,
  heuresFixeesEtALaSuite,
  IMPOSE,
  JOUR_PASSE,
  journeeDefaite,
  LES_PLUS_RAPIDES,
  libreDurant,
  MA_JOURNEE,
  MAINTENANT_DEBUT,
  majuscule,
  MOTS_OU_DU_JOUR,
  MOTS_TEMPS_DU_JOUR,
  NON_LA_GARDER,
  ORDRE_CE_QUI_FLOTTE,
  ORDRE_LES_PLUS_RAPIDES,
  ORDRE_PAR_PROJET,
  OU_A,
  OUI_DEFAIRE,
  PAR_PROJET,
  PREPARER_MA_JOURNEE,
  REMETTRE_A_LA_SUITE,
  REPLIER_LA_RECHERCHE,
  retirerDeLaJournee,
  RIEN_A_PROPOSER,
  RIEN_NE_CORRESPOND,
  SANS_PROJET,
  TEMPS_REMPLI,
  TOUCHE_UNE_HEURE,
  TRIER_CE_QUE_TU_PEUX_FAIRE,
  tropLonguesPourCeQuiReste,
} from '../../partage/vocabulaire';
import { signalerLePanneauDuJour } from '../../aide';
import { useCocher } from '../journeeDuJour';
import { enMots } from './refusDuJour';
import { dureeLisible, heureLisible, jourEnToutesLettres } from './datesDeLAgenda';
import { faitesDeLaJournee, garderLesPossibles } from './journeeAChanger';
import { choixAChanger } from './journeeDuMoment';
import { aAjouter, ceQuElleEst, chercher, choixDeLaJournee, heureTapee, heuresDuJour, raisonsDeLaTache, tempsDeLaJournee } from './journeeEnCours';
import { DEBUTS_DE_LA_QUESTION } from './questionDuJour';
import { JaugeDuJour, TempsPris } from './TempsDeLaJournee';
import { choisiesDans, dansLOrdreDuServeur, lesPlusRapides, parProjet, TRIS, type Tri } from './trisDuJour';
import { useJourneeAComposer } from './useJourneeAComposer';
import { teinteDuProjet } from '../../apparence';
import './PreparerUnJour.css';

/**
 * « Préparer ma journée » (chantier `revue-design`, ticket 06, maquette `design/agenda.html`,
 * décisions d'Igor du 2026-10-03, `en-attente.md`, « L'Agenda », 1 et 2).
 *
 * Un seul panneau, plus d'étapes :
 *   · en haut, le temps qu'on se donne, où l'on sera, l'heure où l'on commence (repliés) ;
 *   · **Ma journée** : chaque chose à son heure. Une tâche a son heure **fixée** (on la touche
 *     et on tape l'heure) ou se met **à la suite** ; le rendez-vous du jour est imposé ; le temps
 *     libre se voit entre deux (« libre 1 h 30 »). Chaque tâche se retire (×) ;
 *   · **À ajouter** : les premières qui tiennent encore, et « Chercher une tâche » (un champ,
 *     les trois tris, toute la liste) ;
 *   · en bas, « 1 h 30 sur 2 h », la jauge et « C'est ma journée ».
 *
 * Une journée neuve arrive **vide** (décision d'Igor du 2026-10-04, B) : la tête de la liste dit
 * « Ajoute ce que tu veux faire ce jour-là », et chaque tâche s'ajoute par son +. Une journée
 * déjà là se rouvre telle qu'enregistrée, ses heures fixées gardées : chaque tâche que le
 * serveur dit `fixee` repart avec `{ id, heure }`, sinon la réenregistrer la déferait (ticket 05
 * de `flottaison-suite`).
 *
 * **Aujourd'hui se prépare ici aussi** (le lien « Ma journée » de « Maintenant » ouvre l'Agenda
 * sur ce panneau) : « Maintenant » comme heure de début, et chaque tâche déjà enregistrée a son
 * rond pour la cocher. Ce qui est fait reste à sa place, sans ×.
 *
 * Le serveur range (les heures, le temps libre, les rendez-vous) ; le panneau garde les choix.
 * Il n'existe qu'à l'ordinateur : le téléphone a ses propres écrans (`AgendaAuPouce`,
 * `JourneeAuPouce`).
 *
 * Deux habits, les mêmes gestes :
 *   · `tiroir` (par défaut) : un panneau posé à droite de la page, sur toute la hauteur (l'onglet
 *     Journée de « Maintenant ») ;
 *   · `fiche` : dans l'Agenda, une fiche scotchée à droite du mois (chantier `agenda-papier`,
 *     maquette du carnet `agenda.html`, `.prepa`) : « Préparer ma journée » écrit à la main, le
 *     jour et les réglages sur une ligne, les heures sur un fil, chaque chose sur sa carte (fixée,
 *     imposée, à la suite), les trous libres en pointillés, « À ajouter », le total, « C'est ma
 *     journée ». La jauge n'y est pas : le total la dit.
 */

const NOM_DU_TRI: Record<Tri, string> = { flotte: CE_QUI_FLOTTE, projet: PAR_PROJET, rapides: LES_PLUS_RAPIDES };
const ORDRE_DU_TRI: Record<Tri, string> = { flotte: ORDRE_CE_QUI_FLOTTE, projet: ORDRE_PAR_PROJET, rapides: ORDRE_LES_PLUS_RAPIDES };
/** Combien de tâches « À ajouter » montre d'un coup, comme la maquette. */
const A_AJOUTER_COMBIEN = 3;

const teinte = (d: DossierDe): CSSProperties => ({ ['--teinte' as string]: teinteDuProjet(d?.couleur) });

type Reglage = 'temps' | 'ou' | 'debut';

export function PreparerUnJour({
  jour,
  onFermer,
  habit = 'tiroir',
}: {
  jour: string;
  onFermer: (message?: string) => void;
  habit?: 'tiroir' | 'fiche';
}) {
  const fiche = habit === 'fiche';
  const cocher = useCocher();
  // Une journée neuve part des réglages par défaut, les mêmes qu'au téléphone et au + (`JOURNEE_PAR_DEFAUT`).
  const [tempsMin, setTempsMin] = useState<number>(JOURNEE_PAR_DEFAUT.tempsMin);
  // `null` : le serveur choisit (celui de la journée déjà là, sinon celui par défaut).
  const [ou, setOu] = useState<Ou | null>(null);
  const [debut, setDebut] = useState(JOURNEE_PAR_DEFAUT.debut);
  // La journée a été lue une fois.
  const [lue, setLue] = useState(false);
  // Un autre « où » : les choix se trient quand sa liste arrive.
  const [aTrier, setATrier] = useState(false);
  const [reglage, setReglage] = useState<Reglage | null>(null);
  const [recherche, setRecherche] = useState<string | null>(null);
  const [tri, setTri] = useState<Tri>('flotte');
  const [ouverts, setOuverts] = useState<Set<string>>(() => new Set());
  const [defaire, setDefaire] = useState(false);

  // Composer la journée : le même cœur qu'au téléphone (chantier `nettoyage-journee`, ticket 02).
  const journee = useJourneeAComposer({ jour, reglages: { debut, tempsMin, ou }, onFermer, depart: [], actif: lue });
  const { donnees, preparation, enregistrer, enHeure, setEnHeure, message, setMessage } = journee;
  const choisis = journee.liste;
  const aPreparer = donnees ? donnees.jour >= donnees.aujourdhui : false;
  const estAujourdhui = donnees ? donnees.jour === donnees.aujourdhui : false;
  const possibles = donnees?.possibles ?? [];
  const faites = faitesDeLaJournee(donnees?.journee);
  const enregistrees = new Set((donnees?.journee?.taches ?? []).map((t) => t.bulleId));

  // La première lecture, fraîche (pas celle gardée d'une ouverture d'avant, qui ne sait pas ce
  // qui a été coché depuis) : la journée déjà là telle qu'enregistrée, sinon une journée vide.
  useEffect(() => {
    if (!donnees || lue || preparation.isFetching) return;
    setLue(true);
    setOu(donnees.ou);
    const j = donnees.journee;
    if (j) {
      setTempsMin(j.tempsMin);
      setDebut(j.debut);
      // Aujourd'hui, ce qui n'est plus faisable part (bloqué) ; ce qui est fait reste.
      journee.demarrer(donnees.jour === donnees.aujourdhui ? choixAChanger(j.taches, donnees.possibles) : choixDeLaJournee(j.taches));
    } else if (donnees.maintenant) {
      setDebut(donnees.maintenant);
    }
    // `journee.demarrer` change à chaque rendu : la journée ne se pose qu'une fois.
  }, [donnees, lue, preparation.isFetching]);

  /*
   * Ce que le panneau montre, dit au tuto (chantier `aide`, ticket 05, « Planifier ta journée ») :
   * rien de visible. Une fois la journée lue seulement ; le numéro change à chaque ouverture.
   */
  const [ouverture] = useState(() => Date.now());
  const idsChoisis = choisis.map((c) => c.id).join('|');
  useEffect(() => {
    if (!donnees || !lue) return;
    signalerLePanneauDuJour({
      aujourdhui: donnees.jour === donnees.aujourdhui,
      choisies: idsChoisis ? idsChoisis.split('|') : [],
      ouverture,
    });
  }, [donnees, lue, idsChoisis, ouverture]);
  useEffect(() => () => signalerLePanneauDuJour(null), []);

  // Un autre « où » : ce qui n'y est plus possible part, le fait reste.
  useEffect(() => {
    if (!aTrier || !donnees || donnees.ou !== ou) return;
    setATrier(false);
    journee.demarrer(garderLesPossibles(choisis, donnees.possibles, faitesDeLaJournee(donnees.journee)));
    // `journee.demarrer` change à chaque rendu : seul le tri demandé compte.
  }, [aTrier, donnees, ou, choisis]);

  /*
   * À l'ordinateur, le panneau se pose sous la barre du haut, comme la maquette : la barre
   * (les vues, l'Aide) reste visible et touchable. Il suit le haut du corps de page quand on
   * fait défiler, jusqu'au bord de la fenêtre.
   */
  const [haut, setHaut] = useState(0);
  useLayoutEffect(() => {
    const corps = document.querySelector('.cadre-corps');
    if (!corps) return;
    const mesurer = () => setHaut(Math.max(0, Math.round(corps.getBoundingClientRect().top)));
    mesurer();
    document.addEventListener('scroll', mesurer, { capture: true, passive: true });
    window.addEventListener('resize', mesurer);
    return () => {
      document.removeEventListener('scroll', mesurer, { capture: true });
      window.removeEventListener('resize', mesurer);
    };
  }, []);

  // Échap referme le panneau, comme une fiche (le champ d'heure le garde pour lui).
  useEchap(true, onFermer);

  const defaireLeJour = useMutation({
    mutationFn: () => api.defaireLeJour(jour),
    onSuccess: () => journee.relireEtFermer(journeeDefaite(jourEnToutesLettres(jour))),
    onError: (e) => setMessage(enMots(e)),
  });

  // Le temps pris : les durées de la journée lue, puis celles des possibles.
  const { utilise, reste } = tempsDeLaJournee(
    choisis,
    [...(donnees?.journee?.taches ?? []).map((t) => [t.bulleId, t.dureeMin] as const), ...possibles.map((p) => [p.id, p.minutes] as const)],
    tempsMin
  );
  const possibleDe = new Map(possibles.map((p) => [p.id, p]));

  const ajouterLaTache = (p: PossibleDuJour) => void journee.ajouterLaTache(p, reste);

  function changerDeOu(v: Ou) {
    setReglage(null);
    if (v === ou) return;
    setOu(v);
    if (choisis.length > 0) setATrier(true);
  }

  function ouvrirOuReplier(cle: string) {
    setOuverts((avant) => {
      const suite = new Set(avant);
      if (!suite.delete(cle)) suite.add(cle);
      return suite;
    });
  }

  const tempsLisible = MOTS_TEMPS_DU_JOUR.find(([v]) => v === tempsMin)?.[1] ?? dureeLisible(tempsMin);
  const debutLisible = debut === donnees?.maintenant ? MAINTENANT_DEBUT.toLocaleLowerCase('fr-FR') : heureLisible(debut);

  const { creneaux } = journee;
  const compte = heuresDuJour(creneaux);
  const ajouts = aAjouter(possibles, choisis, reste, A_AJOUTER_COMBIEN);
  const pris = new Set(choisis.map((c) => c.id));
  const cherchees = recherche === null ? [] : chercher(possibles, recherche).filter((p) => !pris.has(p.id));

  /** Une tâche de la liste cherchée : un toucher l'ajoute ; ce qui ne tient plus se grise. */
  const ligneCherchee = (p: PossibleDuJour, avecProjet: boolean) => {
    const trop = p.minutes > reste;
    return (
      <li key={p.id}>
        <button
          type="button"
          className={`preparer-tache${trop ? ' est-de-trop' : ''}`}
          aria-pressed={false}
          aria-label={ajouterALaJournee(p.titre)}
          data-tuto="preparer-tache"
          data-tache={p.id}
          // Trop longue pour ce qui reste : le tuto mène d'abord au temps (chantier `aide`, ticket 05).
          data-de-trop={trop ? 'oui' : undefined}
          onClick={() => ajouterLaTache(p)}
        >
          <span className="preparer-tache-titre">{p.titre}</span>
          <span className="preparer-tache-quoi">{ceQuElleEst(p, avecProjet, trop)}</span>
        </button>
      </li>
    );
  };

  /** Le temps qu'on se donne, où l'on sera, l'heure où l'on commence : trois boutons qui déplient leur choix. */
  const reglagesDuJour = (ouLu: Ou) => {
    // Au milieu de la ligne de la fiche, les mots s'écrivent en minuscules (« 2 h · chez moi ou dehors »).
    const mot = (m: string) => (fiche ? m.toLocaleLowerCase('fr-FR') : m);
    return [
      <button
        key="temps"
        type="button"
        className="preparer-reglage"
        aria-expanded={reglage === 'temps'}
        data-tuto="preparer-temps"
        onClick={() => setReglage(reglage === 'temps' ? null : 'temps')}
      >
        {mot(tempsLisible)}
      </button>,
      <button key="ou" type="button" className="preparer-reglage" aria-expanded={reglage === 'ou'} onClick={() => setReglage(reglage === 'ou' ? null : 'ou')}>
        {mot(MOTS_OU_DU_JOUR[ou ?? ouLu])}
      </button>,
      <button key="debut" type="button" className="preparer-reglage" aria-expanded={reglage === 'debut'} onClick={() => setReglage(reglage === 'debut' ? null : 'debut')}>
        {mot(desHeure(debutLisible))}
      </button>,
    ];
  };

  return (
    <aside
      className={`preparer${fiche ? ' preparer--fiche' : ''}`}
      aria-label={`${PREPARER_MA_JOURNEE}, ${jourEnToutesLettres(jour)}`}
      data-tuto="preparer"
      data-lue={lue ? 'oui' : 'non'}
      style={fiche ? undefined : { top: haut }}
    >
      {fiche && <span className="preparer-scotch" aria-hidden="true" />}
      <header className="preparer-tete">
        <div className="preparer-tete-mots">
          {fiche ? (
            <h2>{PREPARER_MA_JOURNEE}</h2>
          ) : (
            <>
              <p className="preparer-sur-titre">{PREPARER_MA_JOURNEE}</p>
              <h2>{majuscule(jourEnToutesLettres(jour))}</h2>
            </>
          )}
        </div>
        <button type="button" className="preparer-fermer" aria-label="Fermer" onClick={() => onFermer()}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </header>

      {/* Sur la fiche, le jour et ses réglages tiennent sur une ligne, comme la maquette (`.p-sous`). */}
      {fiche && (
        <p className="preparer-sous">
          <span>{majuscule(jourEnToutesLettres(jour))}</span>
          {donnees &&
            aPreparer &&
            reglagesDuJour(donnees.ou).map((r) => (
              <span key={r.key}>
                <span aria-hidden="true"> · </span>
                {r}
              </span>
            ))}
        </p>
      )}

      {donnees && !aPreparer && <p className="preparer-message">{JOUR_PASSE}</p>}

      {donnees && aPreparer && (
        <>
          {!fiche && <div className="preparer-reglages">{reglagesDuJour(donnees.ou)}</div>}
          {reglage === 'temps' && (
            <div className="preparer-puces">
              {MOTS_TEMPS_DU_JOUR.map(([v, l]) => (
                <button
                  key={v}
                  type="button"
                  className={tempsMin === v ? 'est-choisi' : ''}
                  aria-pressed={tempsMin === v}
                  data-tuto="preparer-temps-choix"
                  onClick={() => {
                    setTempsMin(v);
                    setReglage(null);
                  }}
                >
                  {l}
                </button>
              ))}
            </div>
          )}
          {reglage === 'ou' && (
            <div className="preparer-puces">
              {OU.map((v) => (
                <button key={v} type="button" className={ou === v ? 'est-choisi' : ''} aria-pressed={ou === v} onClick={() => changerDeOu(v)}>
                  {MOTS_OU_DU_JOUR[v]}
                </button>
              ))}
            </div>
          )}
          {reglage === 'debut' && (
            <div className="preparer-puces">
              {donnees.maintenant && (
                <button
                  type="button"
                  className={debut === donnees.maintenant ? 'est-choisi' : ''}
                  aria-pressed={debut === donnees.maintenant}
                  onClick={() => {
                    setDebut(donnees.maintenant!);
                    setReglage(null);
                  }}
                >
                  {MAINTENANT_DEBUT}
                </button>
              )}
              {DEBUTS_DE_LA_QUESTION.map((d) => (
                <button
                  key={d}
                  type="button"
                  className={debut === d ? 'est-choisi' : ''}
                  aria-pressed={debut === d}
                  onClick={() => {
                    setDebut(d);
                    setReglage(null);
                  }}
                >
                  {heureLisible(d)}
                </button>
              ))}
              <label className="preparer-autre-heure">
                {OU_A}
                <ChampDHeure key={debut} valeur={debut} etiquette={OU_A} onEcrire={(h) => setDebut(h)} onQuitter={() => undefined} />
              </label>
            </div>
          )}
          {donnees.impose.echeances.length > 0 && (
            <div className="preparer-echeances">
              {donnees.impose.echeances.map((e) => (
                <span key={e.bulleId} className="preparer-echeance" style={teinte(e.dossier)}>
                  {echeanceDe(e.titre)}
                </span>
              ))}
            </div>
          )}

          <div className="preparer-defile">
            {/*
              Vide, la tête dit quoi faire ; avec des tâches, « Ma journée » (Igor, 2026-10-04, B). Sur
              la fiche de l'Agenda, les heures sur leur fil suffisent : la tête ne parle que vide.
            */}
            {(!fiche || choisis.length === 0) && (
              <div className={`preparer-rubrique${choisis.length === 0 ? ' preparer-rubrique--vide' : ''}`}>
                <span>{choisis.length === 0 ? AJOUTE_CE_QUE_TU_VEUX : MA_JOURNEE}</span>
                {choisis.length > 0 && <small>{TOUCHE_UNE_HEURE}</small>}
              </div>
            )}
            {choisis.length > 0 && creneaux.length === 0 && <p className="preparer-rien preparer-attente">…</p>}
            <ul className="preparer-journee">
              {creneaux.map((c) => {
                const libre = c.libreApres && c.libreApres > 0 ? (
                  <li key={`libre-${c.sorte}-${c.id}`} className="preparer-libre">
                    <span className="preparer-libre-points" aria-hidden="true">
                      · · ·
                    </span>
                    {libreDurant(dureeLisible(c.libreApres))}
                  </li>
                ) : null;
                if (c.sorte === 'rendez_vous') {
                  const duree = c.minutes > 0 ? dureeLisible(c.minutes) : null;
                  return [
                    <li key={`rdv-${c.id}`} className="preparer-ligne preparer-ligne--rdv" data-rdv={c.id}>
                      <span className="preparer-quand preparer-quand--rdv">{heureLisible(c.heure)}</span>
                      {fiche ? (
                        <span className="preparer-carte">
                          <span className="preparer-quoi">
                            <em className="preparer-sorte">{IMPOSE}</em>
                            <b>{c.titre}</b>
                            {duree && <small>{duree}</small>}
                          </span>
                        </span>
                      ) : (
                        <span className="preparer-quoi">
                          <b>{c.titre}</b>
                          <small>{[IMPOSE, duree].filter(Boolean).join(' · ')}</small>
                        </span>
                      )}
                    </li>,
                    libre,
                  ];
                }
                const faite = faites.has(c.id);
                const p = possibleDe.get(c.id);
                const dossier = p?.dossier ?? donnees.journee?.taches.find((t) => t.bulleId === c.id)?.dossier ?? null;
                const rond = estAujourdhui && enregistrees.has(c.id);
                const corps = (
                  <>
                    {rond && (
                      <button
                        type="button"
                        className={`preparer-rond${faite ? ' est-coche' : ''}`}
                        aria-pressed={faite}
                        aria-label={cocherDansLaJournee(c.titre, faite)}
                        data-tuto="journee-rond"
                        data-journee-rond={c.id}
                        disabled={cocher.isPending}
                        onClick={() => cocher.mutate({ id: c.id, fait: !faite })}
                      >
                        <span aria-hidden="true">{faite ? '✓' : ''}</span>
                      </button>
                    )}
                    <span className="preparer-quoi">
                      {fiche && <em className="preparer-sorte">{faite ? FAITE : c.fixee ? FIXEE : A_LA_SUITE}</em>}
                      <b>{c.titre}</b>
                      <small>{[dureeLisible(c.minutes), dossier?.titre, ...(p ? raisonsDeLaTache(p) : [])].filter(Boolean).join(' · ')}</small>
                    </span>
                    {/* ⇄ est parti avec la proposition (Igor, 2026-10-04, B) : × puis + suffisent. */}
                    {!faite && (
                      <button type="button" className="preparer-geste" aria-label={retirerDeLaJournee(c.titre)} onClick={() => journee.retirerLaTache(c.id)}>
                        <svg width={fiche ? 14 : 18} height={fiche ? 14 : 18} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                          <path d="M6 6l12 12M18 6L6 18" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </button>
                    )}
                  </>
                );
                return [
                  <li
                    key={`tache-${c.id}`}
                    className={`preparer-ligne${faite ? ' est-faite' : ''}${!faite && !c.fixee ? ' est-a-la-suite' : ''}${c.horsDuJour ? ' est-hors-du-jour' : ''}`}
                    data-tuto="preparer-creneau"
                    data-tache={c.id}
                  >
                    <span className="preparer-quand">
                      {faite ? (
                        <span className="preparer-heure preparer-heure--tenue">{heureLisible(c.heure)}</span>
                      ) : enHeure === c.id ? (
                        <ChampDHeure
                          valeur={c.heure}
                          etiquette={changerLHeureDe(c.titre)}
                          onEcrire={(h) => void journee.fixerLHeure(c.id, h)}
                          onQuitter={() => setEnHeure(null)}
                          aLaSuite={c.fixee ? () => void journee.fixerLHeure(c.id, null) : undefined}
                          auFocus
                          memeHeureSurEntree={!c.fixee}
                        />
                      ) : (
                        <button
                          type="button"
                          className={`preparer-heure${c.fixee ? ' est-fixee' : ''}`}
                          aria-label={changerLHeureDe(c.titre)}
                          onClick={() => {
                            setMessage(null);
                            setEnHeure(c.id);
                          }}
                        >
                          {c.horsDuJour ? APRES_MINUIT : heureLisible(c.heure)}
                        </button>
                      )}
                      {!fiche && (
                        <span className={`preparer-sorte${c.fixee && !faite ? ' est-fixee' : ''}`}>
                          {faite ? (
                            FAITE
                          ) : c.fixee ? (
                            <>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                                <path d="M9 4h6l-1 6 3 3H7l3-3z" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M12 13v7" strokeWidth="2.4" strokeLinecap="round" />
                              </svg>
                              {FIXEE}
                            </>
                          ) : (
                            A_LA_SUITE
                          )}
                        </span>
                      )}
                    </span>
                    {fiche ? <span className="preparer-carte">{corps}</span> : corps}
                  </li>,
                  libre,
                ];
              })}
            </ul>

            <div className="preparer-rubrique">
              <span>{A_AJOUTER}</span>
              <button
                type="button"
                className="preparer-lien"
                data-tuto="preparer-chercher"
                aria-expanded={recherche !== null}
                onClick={() => setRecherche(recherche === null ? '' : null)}
              >
                {recherche === null ? CHERCHER_UNE_TACHE : REPLIER_LA_RECHERCHE}
              </button>
            </div>
            {possibles.length === 0 ? (
              <p className="preparer-rien">{RIEN_A_PROPOSER}</p>
            ) : recherche === null ? (
              <>
                <ul className="preparer-ajouts">
                  {ajouts.montrees.map((p) =>
                    fiche ? (
                      // Sur la fiche, toute la ligne s'ajoute d'un clic : « + Résilier l'eau 10 min… ».
                      <li key={p.id} className="preparer-ajout">
                        <button
                          type="button"
                          className="preparer-plus"
                          aria-label={ajouterALaJournee(p.titre)}
                          aria-pressed={false}
                          data-tuto="preparer-tache"
                          data-tache={p.id}
                          onClick={() => ajouterLaTache(p)}
                        >
                          <span aria-hidden="true">+</span> <b>{p.titre}</b> <small>{ceQuElleEst(p, true, false)}</small>
                        </button>
                      </li>
                    ) : (
                      <li key={p.id} className="preparer-ajout">
                        <span className="preparer-quoi">
                          <b>{p.titre}</b> <small>· {ceQuElleEst(p, true, false)}</small>
                        </span>
                        <button
                          type="button"
                          className="preparer-plus"
                          aria-label={ajouterALaJournee(p.titre)}
                          aria-pressed={false}
                          data-tuto="preparer-tache"
                          data-tache={p.id}
                          onClick={() => ajouterLaTache(p)}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                            <path d="M12 5v14M5 12h14" strokeWidth="2.4" strokeLinecap="round" />
                          </svg>
                        </button>
                      </li>
                    )
                  )}
                </ul>
                {ajouts.tropLongues > 0 && (
                  <p className="preparer-note">{reste > 0 ? tropLonguesPourCeQuiReste(ajouts.tropLongues, dureeLisible(reste)) : TEMPS_REMPLI}</p>
                )}
              </>
            ) : (
              <div className="preparer-recherche">
                <input
                  type="search"
                  className="preparer-champ"
                  value={recherche}
                  placeholder={CHERCHER_UNE_TACHE}
                  aria-label={CHERCHER_UNE_TACHE}
                  autoFocus
                  onChange={(e) => setRecherche(e.target.value)}
                />
                <div className="preparer-tris" role="group" aria-label={TRIER_CE_QUE_TU_PEUX_FAIRE}>
                  {TRIS.map((t) => (
                    <button key={t} type="button" className={tri === t ? 'est-choisi' : ''} aria-pressed={tri === t} data-tri={t} onClick={() => setTri(t)}>
                      {NOM_DU_TRI[t]}
                    </button>
                  ))}
                </div>
                <p className="preparer-note preparer-ordre">{ORDRE_DU_TRI[tri]}</p>
                {cherchees.length === 0 ? (
                  <p className="preparer-rien">{RIEN_NE_CORRESPOND}</p>
                ) : tri === 'projet' ? (
                  parProjet(cherchees).map((g) => {
                    const ouvert = ouverts.has(g.cle) || (recherche ?? '').trim() !== '';
                    return (
                      <section key={g.cle} className="preparer-groupe" style={teinte(g.dossier)} data-groupe={g.cle}>
                        <button type="button" className="preparer-groupe-tete" aria-expanded={ouvert} onClick={() => ouvrirOuReplier(g.cle)}>
                          <span className="preparer-groupe-titre">{g.dossier?.titre ?? SANS_PROJET}</span>
                          <span className="preparer-groupe-compte">
                            {choisiesEtPossibles(choisiesDans(g, choisis.map((c) => c.id)), g.taches.length)} {ouvert ? '▴' : '▾'}
                          </span>
                        </button>
                        {ouvert && <ul className="preparer-taches">{g.taches.map((p) => ligneCherchee(p, false))}</ul>}
                      </section>
                    );
                  })
                ) : (
                  <ul className="preparer-taches">
                    {(tri === 'rapides' ? lesPlusRapides(cherchees) : dansLOrdreDuServeur(cherchees)).map((p) => ligneCherchee(p, true))}
                  </ul>
                )}
              </div>
            )}
            {donnees.prevuesAilleurs > 0 && (
              <p className="preparer-note">
                {donnees.prevuesAilleurs === 1
                  ? 'Une tâche déjà prévue un autre jour n’apparaît pas ici.'
                  : `${donnees.prevuesAilleurs} tâches déjà prévues un autre jour n’apparaissent pas ici.`}
              </p>
            )}
          </div>

          <footer className="preparer-pied">
            {message && (
              <p className="preparer-message" role="status">
                {message}
              </p>
            )}
            {journee.apresMinuit && (
              <p className="preparer-message" role="status">
                {FINIRAIT_APRES_MINUIT}
              </p>
            )}
            <div className="preparer-compteur">
              {fiche ? (
                <span className="preparer-pris">
                  {dureeLisible(utilise)} <small>sur {dureeLisible(tempsMin)}</small>
                </span>
              ) : (
                <span>
                  <TempsPris utilise={utilise} tempsMin={tempsMin} />
                </span>
              )}
              <span>{heuresFixeesEtALaSuite(compte.fixees, compte.aLaSuite)}</span>
            </div>
            {!fiche && <JaugeDuJour utilise={utilise} tempsMin={tempsMin} />}
            <button type="button" className="preparer-fort" data-tuto="preparer-fort" disabled={!journee.pret} onClick={() => enregistrer.mutate()}>
              {C_EST_MA_JOURNEE}
            </button>
            {donnees.journee &&
              (defaire ? (
                <div className="preparer-defaire">
                  <p>{DEFAIRE_NOTE}</p>
                  <button type="button" className="preparer-doux" disabled={defaireLeJour.isPending} onClick={() => defaireLeJour.mutate()}>
                    {OUI_DEFAIRE}
                  </button>
                  <button type="button" className="preparer-doux" onClick={() => setDefaire(false)}>
                    {NON_LA_GARDER}
                  </button>
                </div>
              ) : (
                <button type="button" className="preparer-doux" onClick={() => setDefaire(true)}>
                  {DEFAIRE_CETTE_JOURNEE}
                </button>
              ))}
          </footer>
        </>
      )}
    </aside>
  );
}

/**
 * Le champ d'une heure qu'on tape (« 14:05 ») : rien ne s'écrit pendant la frappe, seulement sur
 * Entrée ou en quittant le champ, et seulement une heure entière (fiche React et Vite, « une date
 * tapée au clavier s'enregistre à chaque chiffre »). Échap rend l'heure d'avant sans fermer le
 * panneau. « Remettre à la suite » (une tâche fixée) ne fait pas quitter le champ avant d'agir.
 * Repris au téléphone par « Propose-moi » et « Je choisis » (`CreneauxDeLaJournee`), pour fixer
 * une heure de la même façon.
 */
export function ChampDHeure({
  valeur,
  etiquette,
  onEcrire,
  onQuitter,
  aLaSuite,
  auFocus = false,
  memeHeureSurEntree = false,
}: {
  valeur: string;
  etiquette: string;
  onEcrire: (heure: string) => void;
  onQuitter: () => void;
  aLaSuite?: () => void;
  auFocus?: boolean;
  /** Entrée sur l'heure telle quelle l'écrit quand même : une tâche à la suite s'y fixe. */
  memeHeureSurEntree?: boolean;
}) {
  const fini = useRef(false);
  // Une touche pressée dans le champ veut dire « frappe » ; un choix au sélecteur du téléphone
  // (sans touche) s'écrit aussitôt (fiche React et Vite).
  const auClavier = useRef(false);
  function finir(champ: HTMLInputElement, parEntree: boolean) {
    if (fini.current) return;
    fini.current = true;
    const h = heureTapee(champ.value, champ.validity.badInput);
    if (h && (h !== valeur || (parEntree && memeHeureSurEntree))) onEcrire(h);
    else {
      champ.value = valeur;
      onQuitter();
      fini.current = false;
    }
  }
  return (
    <span className="preparer-champ-heure">
      <input
        type="time"
        step={300}
        defaultValue={valeur}
        aria-label={etiquette}
        autoFocus={auFocus}
        onPointerDown={() => {
          auClavier.current = false;
        }}
        onChange={(e) => {
          if (!auClavier.current) finir(e.currentTarget, false);
        }}
        onKeyDown={(e) => {
          auClavier.current = true;
          if (e.key === 'Enter') {
            e.preventDefault();
            finir(e.currentTarget, true);
          } else if (e.key === 'Escape') {
            e.stopPropagation();
            fini.current = true;
            onQuitter();
          }
        }}
        onBlur={(e) => finir(e.currentTarget, false)}
      />
      {aLaSuite && (
        <button
          type="button"
          className="preparer-a-la-suite"
          onPointerDown={(e) => e.preventDefault()}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            fini.current = true;
            aLaSuite();
          }}
        >
          {REMETTRE_A_LA_SUITE}
        </button>
      )}
    </span>
  );
}

